/**
 * The device list.
 *
 * Every decision about what to show lives in deviceViewModel, which is tested
 * exhaustively. This file renders the result and does no reasoning of its own.
 */

import { useState } from 'react';

import { AddCamera } from './AddCamera.tsx';
import { ScanForCameras } from './ScanForCameras.tsx';
import { ApiError, reissueClaimCode, reprovisionDevice } from './api.ts';
import { routeHref } from './routing.ts';
import {
  deviceListViewModel,
  type DeviceSummary,
  type DeviceViewModel,
  type FetchState,
} from './deviceViewModel.ts';

export interface IssuedClaim {
  claimCode: string;
  claimCodeExpiresAt: string;
  deviceName: string;
}

interface Props {
  devices: DeviceSummary[];
  fetchState: FetchState;
  now: number;
  onChanged: () => void;
  onClaimIssued: (claim: IssuedClaim) => void;
}

const HEALTH_LABEL: Record<DeviceViewModel['health'], string> = {
  online: 'Online',
  offline: 'Offline',
  'never-connected': 'Never connected',
};

function DeviceCard({
  device,
  onChanged,
  onClaimIssued,
}: {
  device: DeviceViewModel;
  onChanged: () => void;
  onClaimIssued: (claim: IssuedClaim) => void;
}): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Generates a code for a device nobody has commissioned yet.
   *
   * The path that was missing: a device provisioned an hour ago whose code was
   * mistyped five times, or simply lost, had no route back except the database.
   */
  async function generateCode(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const result = await reissueClaimCode(device.id);
      onClaimIssued({
        claimCode: result.claimCode,
        claimCodeExpiresAt: result.claimCodeExpiresAt,
        deviceName: device.name,
      });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not generate a claim code.');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Re-provisioning a device that is already claimed.
   *
   * Destructive and confirmed, because it revokes every token and hangs up on
   * the appliance in the field — which stops talking to the cloud until someone
   * re-claims it. For a replacement, a factory reset, or a suspected compromise.
   */
  async function reprovision(): Promise<void> {
    const confirmed = window.confirm(
      `Re-provision ${device.name}?\n\n` +
        'This revokes its access immediately and disconnects it. The device will ' +
        'stop reaching the cloud until someone claims it again with the new code.\n\n' +
        'Recording on the device itself is unaffected.',
    );
    if (!confirmed) return;

    setBusy(true);
    setError(null);
    try {
      const result = await reprovisionDevice(device.id, 'operator_revoked');
      onClaimIssued({
        claimCode: result.claimCode,
        claimCodeExpiresAt: result.claimCodeExpiresAt,
        deviceName: device.name,
      });
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not re-provision the device.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="card">
      <div className="card__body">
        <div className="device__top">
          <div>
            {/* The card is a summary; the detail page is where a device is
                actually worked on. The name is the obvious thing to click. */}
            <h3 className="device__name">
              <a href={routeHref('devices', device.id)}>{device.name}</a>
            </h3>
            {device.siteName ? <p className="device__site">{device.siteName}</p> : null}
          </div>
          <span className={`status status--${device.health}`}>
            <span className="status__dot" />
            {HEALTH_LABEL[device.health]}
          </span>
        </div>

        {/* Distinct from health: a device can be unclaimed and perfectly healthy,
            and "nobody has installed this yet" is a different job to do. */}
        {device.claimState === 'unclaimed' ? (
          <p style={{ marginTop: 'var(--s3)' }}>
            <span className="pill">Awaiting claim</span>
          </p>
        ) : null}

        <p className="device__seen">Last seen {device.lastSeenLabel}</p>

        <p className="cameras__summary">
          {device.cameraSummary.total === 0
            ? 'No cameras yet'
            : `${device.cameraSummary.streaming} of ${device.cameraSummary.total} streaming`}
        </p>

        {device.cameras.length > 0 ? (
          <ul className="cameras">
            {device.cameras.map((camera) => (
              <li key={camera.id} className={`camera camera--${camera.state}`}>
                <span className="camera__name">{camera.name ?? camera.id}</span>
                <span className="camera__state">{camera.state}</span>
              </li>
            ))}
          </ul>
        ) : null}

        {/* Staleness sits beside the data, never in place of it. */}
        {device.stale && device.staleReason ? (
          <p className="stale">{device.staleReason}</p>
        ) : null}

        {error ? (
          <p className="notice notice--error" role="alert" style={{ marginTop: 'var(--s3)' }}>
            {error}
          </p>
        ) : null}
      </div>

      <div className="card__foot">
        <a className="btn btn--sm" href={routeHref('devices', device.id)}>
          Manage
        </a>
        {/*
          * Scan before Add, here as on the detail page.
          *
          * These are alternatives for the same task, and offering only the
          * hand-typing one is backwards — the card previously did exactly that,
          * so the better path existed and was invisible unless you opened the
          * device.
          */}
        <ScanForCameras deviceId={device.id} onAdded={onChanged} />
        <AddCamera deviceId={device.id} onAdded={onChanged} />

        {device.claimState === 'unclaimed' ? (
          <button type="button" className="btn btn--sm" disabled={busy} onClick={() => void generateCode()}>
            {busy ? 'Working…' : 'Generate claim code'}
          </button>
        ) : (
          <button
            type="button"
            className="btn btn--sm btn--danger"
            disabled={busy}
            onClick={() => void reprovision()}
          >
            {busy ? 'Working…' : 'Re-provision'}
          </button>
        )}
      </div>
    </li>
  );
}

export function DeviceList({
  devices,
  fetchState,
  now,
  onChanged,
  onClaimIssued,
}: Props): React.JSX.Element {
  const model = deviceListViewModel(devices, fetchState, now);

  return (
    <section className="section">
      {/* A notice, not a replacement. The list below stays rendered. */}
      {model.connectionNotice ? (
        <p className="notice notice--warn" role="status" style={{ marginBottom: 'var(--s4)' }}>
          {model.connectionNotice}
        </p>
      ) : null}

      {model.showLoadingPlaceholder ? <p className="empty">Loading devices…</p> : null}

      {model.showEmptyState ? (
        <div className="empty">
          <p className="empty__title">No devices yet</p>
          <p>Use “Provision device” above to get a claim code for the installer.</p>
        </div>
      ) : null}

      {model.devices.length > 0 ? (
        <ul className="grid" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {model.devices.map((device) => (
            <DeviceCard
              key={device.id}
              device={device}
              onChanged={onChanged}
              onClaimIssued={onClaimIssued}
            />
          ))}
        </ul>
      ) : null}
    </section>
  );
}
